import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../gather-services/loading.service';
import { ApiWithLoadingService } from '../gather-services/api-with-loading.service';
import { GatherApiService } from '../gather-services/gather-api.service';
import { SharedDataService } from '../gather-services/shared-data.service';
import { GatherStateService } from '../gather-services/gather-state.service';
import {
  Availability,
  Gather,
  JobPosting,
  PersonPortfolioUrl
} from "../gather-typings";
import { of, Subscription} from "rxjs";
import { catchError } from "rxjs/operators";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, FormControl, FormArray, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { PreInterviewQuestionsService } from "../../pre-interview-questions/pre-interview-questions.service";
import { FeatureService, TalentAppFeatures } from "../../service/feature.service";
import {Country} from "../enums";
import { TalentHappyPathDataService } from 'src/app/talent-happy-path/shared/service/talent-happy-path-data-service';
import { FileUploadUserType, PersonUriType, TalentProfileStartAvailability } from 'src/app/enums';
import { urlValidator } from '../../talent-happy-path/shared/validators/urlValidator';
import { LocationPickerComponent } from "../../talent-happy-path/shared/components/location-picker/location-picker.component";
import { GatherHomeLocationService } from "../gather-services/gather-home-location.service";

const NOT_FOUND_RESPONSE: number[] = [404, 408, 421, 422];
const NOT_ACCESSIBLE_RESPONSE: number[] = [407, 423];

const totalLinksValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const profileUrls = control.get('profileUrls') as FormArray;
  const portfolioUrls = control.get('portfolioUrls') as FormArray;
  const profileCount = profileUrls?.controls.filter(c => !!c.get('url')?.value?.trim()).length || 0;
  const portfolioCount = portfolioUrls?.controls.filter(c => !!c.get('url')?.value?.trim()).length || 0;
  return profileCount + portfolioCount > 6 ? { tooManyLinks: true } : null;
};

@Component({
    selector: 'app-availability',
    templateUrl: './availability.component.html',
    styleUrls: ['./availability.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [GatherHomeLocationService]
})
export class AvailabilityComponent implements OnInit {

  @ViewChild(LocationPickerComponent)
  set locationPicker(locationPicker: LocationPickerComponent) {
    this.currentLocationPicker = locationPicker;
    this.homeLocationService.prefill(locationPicker, this.form);
  }
  private currentLocationPicker: LocationPickerComponent = null;
  trackingId: string = null;
  jobPosting: JobPosting = null;
  gather: Gather = null;
  talent: Talent = null;
  availability: Availability = {};
  fromTextMessage: boolean = false;
  private subscriptions = new Subscription();
  private readonly lastVerifiedUrlByGroup = new WeakMap<FormGroup, string>();
  private verifyingCount = 0;
  form: UntypedFormGroup = this.formBuilder.group(
    {
      hourlyRate: '',
      projectRate: '',
      salary: '',
      availabilityType: [0],
      availabilityDate: null,
      numWeeksNoticeNeeded: new FormControl('', [Validators.pattern('^[1-9][0-9]*$')]),
      notes: '',
      resumeUpload: null,
      permissionToSubmit: null,
      profileUrls: new FormArray([]),
      portfolioUrls: new FormArray([]),
    },
    { validators: totalLinksValidator }
  );
  loading = true;
  hasPreInterviewQuestions: boolean = false;
  currentResumeFileName: string;
  preInterviewFeature = false;
  isJapanOrder: boolean;
  minDate: Date = new Date();
  maxDate: Date = new Date(this.minDate.getFullYear() + 2, this.minDate.getMonth(), this.minDate.getDate());
  talentPortfolioURLs: PersonPortfolioUrl[] = [];
  newPortfolioUrl: PersonPortfolioUrl = { personUriId: undefined, personId: undefined, uri: '' };
  showPortfolioLinks = true;
  errorMessage: string='';
  resumeUploaded: boolean = false;
  resumeUploading: boolean = false;
  fileUploadUserType: FileUploadUserType = FileUploadUserType.GATHER;
  availabilityDate: Date;
  talentProfileStartAvailability = TalentProfileStartAvailability;
  payTransparencyFeature = false;


  constructor(private router: Router,
              private route: ActivatedRoute,
              private loadingService: LoadingService,
              private api: ApiWithLoadingService,
              private gatherApiService: GatherApiService,
              private sharedDataService: SharedDataService,
              private gatherStateService: GatherStateService,
              private preInterviewQuestionService: PreInterviewQuestionsService,
              private formBuilder: UntypedFormBuilder,
              private featureService: FeatureService,
              private talentHappyPathDataService: TalentHappyPathDataService,
              private homeLocationService: GatherHomeLocationService) {
    this.preInterviewFeature = this.featureService.getFeature(TalentAppFeatures.PRE_INTERVIEW_QUESTIONS)
    this.payTransparencyFeature = this.featureService.getFeature(TalentAppFeatures.APPLICANT_PAY_TRANSPARENCY_FEATURE)
  }

  ngOnInit(): void {
    this.fromTextMessage =
      this.route.snapshot.queryParamMap.has('textMessage') && this.route.snapshot.queryParamMap.get('textMessage') === 'true';

    this.subscriptions.add(
      this.sharedDataService.trackingId.subscribe(id => {
        this.trackingId = id;
      })
    );

    this.subscriptions.add(
      this.sharedDataService.talentAvailability.subscribe(availability => {
        this.availability = availability

        if (this.availability.alreadyResponded) {
          this.router.navigate(['gather', this.trackingId, 'confirm'],
            {queryParams: {fromRoute: 'interested'}});
        } else {
          if(this.trackingId !== '' && this.trackingId !== null) {
            this.gatherApiService.updateGatherResponseInterested(this.trackingId, this.fromTextMessage).subscribe();
          }
        }
      })
    );

    this.subscriptions.add(
      this.sharedDataService.jobPosting.subscribe(jobPosting => {
        this.jobPosting = jobPosting;
        this.isJapanOrder = jobPosting.countryId === Country.Japan;
        if (this.jobPosting.orderId != -1) {
          this.subscriptions.add(
            this.preInterviewQuestionService.getSelectedQuestions(this.jobPosting.orderId).subscribe(selectedQuestions => {
              const activeQuestions = selectedQuestions.filter(question => question.isActive)
              this.hasPreInterviewQuestions = activeQuestions.length > 0;
            })
          );
        }
      })
    );

    this.subscriptions.add(
      this.sharedDataService.gather.subscribe(gather => {
        this.gather = gather;
        this.talentHappyPathDataService.gather = gather;
        this.talentHappyPathDataService.homeMarketCountryId = gather.talentHomeMarketCountryId;
        this.talentHappyPathDataService.talent = {
          email: gather.talentEmail,
          personId:gather.talentPersonId,
          firstName: gather.talent?.firstName,
          lastName: gather.talent?.lastName,
          preferredName: gather.talent?.preferredName,
          personTypeId: undefined,
          profileUpdated: undefined,
          resumeUpdated: undefined,
          resumeData: undefined,
          termsAccepted: undefined,
          locationUpdated: undefined,
          smsOptIn: undefined,
          doubleOptIn: undefined,
          newTHPTalent: undefined,
          geoLocation: undefined,
          portfolioUpdated: undefined,
          countryId: gather.talentHomeMarketCountryId
        };

        if (gather.talent) {
          this.talent = {
            email: gather.talentEmail,
            personId: gather.talentPersonId,
            firstName: gather.talent.firstName,
            lastName: gather.talent.lastName,
            preferredName: gather.talent.preferredName,
            personTypeId: undefined,
            profileUpdated: undefined,
            resumeUpdated: undefined,
            resumeData: undefined,
            termsAccepted: undefined,
            locationUpdated: undefined,
            smsOptIn: undefined,
            doubleOptIn: undefined,
            newTHPTalent: undefined,
            geoLocation: undefined,
            portfolioUpdated: undefined,
            countryId: gather.talentHomeMarketCountryId
          };
        }

        if (this.gather.talentPersonId != -1) {
          this.gatherApiService.getTalentPersonProfileUrls(this.gather.talentPersonId).subscribe(uris => {
            this.patchUris(uris || []);
          });

          this.subscriptions.add(
            this.homeLocationService.load(this.trackingId).subscribe(() => {
              this.homeLocationService.prefill(this.currentLocationPicker, this.form);
            })
          );
        } else {
          this.patchUris([]);
        }
      })
    );

    this.gatherStateService.setTitle('gather.interested.title');
  }

  get profileUrls(): FormArray {
    return this.form.get('profileUrls') as FormArray;
  }

  get portfolioUrls(): FormArray {
    return this.form.get('portfolioUrls') as FormArray;
  }

  private countNonEmptyUrls(urls: FormArray): number {
    return urls.controls.filter((group: AbstractControl) => {
      const urlValue = group.get('url')?.value;
      return typeof urlValue === 'string' && urlValue.trim().length > 0;
    }).length;
  }

  get canAddMore(): boolean {
    const totalProfiles = this.countNonEmptyUrls(this.profileUrls);
    const totalPortfolios = this.countNonEmptyUrls(this.portfolioUrls);
    return totalProfiles + totalPortfolios < 6;
  }

  // Disable submit while a resume is uploading, a URL verification is in flight, or the form is
  // invalid (malformed/unreachable URLs, weeks-notice pattern, or the 6-link limit).
  get submitDisabled(): boolean {
    return this.resumeUploading || this.verifyingCount > 0 || this.form.invalid;
  }

  clearResponseCode(group: AbstractControl): void {
    group.get('responseCode')?.setValue(undefined, { emitEvent: false });

    const urlControl = group.get('url');
    const errors = urlControl?.errors;

    if (urlControl && errors?.incorrect) {
      const { incorrect, ...remainingErrors } = errors;
      urlControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    }
  }

  addProfileUrl(data: Partial<PersonPortfolioUrl> = {}): void {
    if (!this.canAddMore) {
      return;
    }

    const group = new FormGroup({
      personUriId: new FormControl(data.personUriId),
      personId: new FormControl(data.personId),
      type: new FormControl(PersonUriType.PROFILE),
      url: new FormControl(data.uri || '', [urlValidator()]),
      responseCode: new FormControl(undefined)
    });

    this.profileUrls.push(group);
    this.lastVerifiedUrlByGroup.set(group, (data.uri || '').trim());
  }

  addPortfolioUrl(data: Partial<PersonPortfolioUrl> = {}): void {
    if (!this.canAddMore) {
      return;
    }

    const group = new FormGroup({
      personUriId: new FormControl(data.personUriId),
      personId: new FormControl(data.personId),
      type: new FormControl(PersonUriType.PORTFOLIO),
      url: new FormControl(data.uri || '', [urlValidator()]),
      password: new FormControl(data.password || '', [Validators.maxLength(100)]),
      showPassword: new FormControl(!!data.password),
      hidePassword: new FormControl(true),
      responseCode: new FormControl(undefined)
    });

    this.portfolioUrls.push(group);
    this.lastVerifiedUrlByGroup.set(group, (data.uri || '').trim());
  }

  removeProfileUrl(index: number): void {
    const group = this.profileUrls.at(index) as FormGroup;
    const personUriId = group.get('personUriId')?.value;
    if (!personUriId) {
      this.profileUrls.removeAt(index);
      if (this.profileUrls.length === 0) {
        this.addProfileUrl();
      }
      this.form.updateValueAndValidity();
      return;
    }

    this.gatherApiService.deletePersonUri(personUriId).subscribe({
      next: () => {
        this.profileUrls.removeAt(index);
        if (this.profileUrls.length === 0) {
          this.addProfileUrl();
        }
        this.form.updateValueAndValidity();
      },
      error: () => {
        this.errorMessage = 'gather.availability.error';
      }
    });
  }

  removePortfolioUrl(index: number): void {
    const group = this.portfolioUrls.at(index) as FormGroup;
    const personUriId = group.get('personUriId')?.value;
    if (!personUriId) {
      this.portfolioUrls.removeAt(index);
      if (this.portfolioUrls.length === 0) {
        this.addPortfolioUrl();
      }
      this.form.updateValueAndValidity();
      return;
    }

    this.gatherApiService.deletePersonUri(personUriId).subscribe({
      next: () => {
        this.portfolioUrls.removeAt(index);
        if (this.portfolioUrls.length === 0) {
          this.addPortfolioUrl();
        }
        this.form.updateValueAndValidity();
      },
      error: () => {
        this.errorMessage = 'gather.availability.error';
      }
    });
  }

  onUrlBlur(group: AbstractControl): void {
    const formGroup = group as FormGroup;
    const url = formGroup.get('url')?.value?.trim();
    if (!url) {
      return;
    }

    if (this.lastVerifiedUrlByGroup.get(formGroup) === url) {
      return;
    }

    const urlControl = formGroup.get('url');
    if (urlControl?.invalid) {
      return;
    }

    this.verifyingCount++;
    this.gatherApiService.verifyPersonUri(url).subscribe({
      next: response => {
        this.verifyingCount--;
        this.lastVerifiedUrlByGroup.set(formGroup, url);
        formGroup.get('responseCode')?.setValue(this.mapResponseCode(response));
        if (response !== 0) {
          if (urlControl?.invalid) {
            formGroup.get('responseCode')?.setValue(0);
          } else {
            urlControl?.setErrors({ incorrect: true });
          }
        }
      },
      error: () => {
        this.verifyingCount--;
        formGroup.get('responseCode')?.setValue(0);
      }
    });
  }

  onShowPasswordChange(group: AbstractControl): void {
    if (!group.get('showPassword')?.value) {
      group.get('password')?.setValue('');
    }
  }

  private mapResponseCode(response: number): number {
    if (response === 0) {
      return 0;
    }
    if (NOT_FOUND_RESPONSE.includes(response)) {
      return 404;
    }
    if (NOT_ACCESSIBLE_RESPONSE.includes(response)) {
      return 403;
    }
    return 500;
  }

  private patchUris(existingUris: PersonPortfolioUrl[]): void {
    this.profileUrls.clear();
    this.portfolioUrls.clear();

    if (!existingUris.length) {
      this.addProfileUrl();
      this.addPortfolioUrl();
      return;
    }

    existingUris.forEach(uri => {
      if (uri.type === PersonUriType.PROFILE) {
        this.addProfileUrl(uri);
      } else {
        this.addPortfolioUrl(uri);
      }
    });

    if (this.profileUrls.length === 0) {
      this.addProfileUrl();
    }

    if (this.portfolioUrls.length === 0) {
      this.addPortfolioUrl();
    }
  }

  private buildAvailabilityUris(): PersonPortfolioUrl[] {
    const profileUris = this.profileUrls.controls
      .map(control => control as FormGroup)
      .filter(group => !!group.get('url')?.value?.trim())
      .map(group => {
        return {
          personUriId: group.get('personUriId')?.value,
          personId: this.gather.talentPersonId,
          uri: group.get('url')?.value.trim(),
          type: PersonUriType.PROFILE
        } as PersonPortfolioUrl;
      });

    const portfolioUris = this.portfolioUrls.controls
      .map(control => control as FormGroup)
      .filter(group => !!group.get('url')?.value?.trim())
      .map(group => {
        return {
          personUriId: group.get('personUriId')?.value,
          personId: this.gather.talentPersonId,
          uri: group.get('url')?.value.trim(),
          type: PersonUriType.PORTFOLIO,
          password: group.get('showPassword')?.value ? group.get('password')?.value : undefined
        } as PersonPortfolioUrl;
      });

    return [...profileUris, ...portfolioUris];
  }

  submitAvailability(): void {
    if (this.submitDisabled) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;

    // Save the home location before submitting, but never let it hold up the gather response
    this.subscriptions.add(
      this.homeLocationService.save(this.trackingId, this.gather.talentPersonId, this.form)
        .pipe(catchError(error => {
          console.error('Error saving home location: ' + error.code);
          return of(undefined);
        }))
        .subscribe(() => this.submitAvailabilityResponse())
    );
  }

  private submitAvailabilityResponse(): void {
    switch (this.form.get('availabilityType').value) {
      case TalentProfileStartAvailability.NOW:
        this.availability.availabilityType = TalentProfileStartAvailability.NOW;
        break;
      case TalentProfileStartAvailability.WITH_NOTICE:
        this.availability.availabilityType = TalentProfileStartAvailability.WITH_NOTICE;
        this.availability.numWeeksNoticeNeeded = parseInt(this.form.get('numWeeksNoticeNeeded').value);
        break;
      case TalentProfileStartAvailability.SPECIFIC_DATE:
        this.availability.availabilityType = TalentProfileStartAvailability.SPECIFIC_DATE;
        this.availability.availabilityDate = this.form.get('availabilityDate').value;
        break;
    }

    this.availability.activelySearchingSelected = true;
    this.availability.trackingId = this.trackingId;
    this.availability.notes = this.form.get('notes').value;
    this.availability.hourlyRate = this.form.get('hourlyRate').value;
    this.availability.annualSalary = this.form.get('salary').value;
    this.availability.projectRate = this.form.get('projectRate').value;
    this.availability.portfolioUrl = this.buildAvailabilityUris();

    if (this.talentHappyPathDataService.resumeDetail) {
      this.talentHappyPathDataService.talent.resumeUpdated = this.resumeUploaded;
      this.talentHappyPathDataService.resumeDetail.personId = this.gather.talentPersonId;

      this.gatherApiService.saveResumeDetail(this.talentHappyPathDataService.resumeDetail).subscribe(
        () => {
          console.log('Updated resume saved successfully');
        },
        error => {
          this.loading = false;
          this.errorMessage = 'gather.availability.error';
          console.error('Error saving updated resume: ' + error.code);
        }
      );
    }

    this.gatherApiService.saveAvailabilityMoats(this.availability.trackingId, this.availability, true)
      .subscribe(() => {
        this.loading = false;
        this.talentHappyPathDataService.postingId = this.jobPosting.jobPostingId;
        this.talentHappyPathDataService.postingInfo = {
          jobPostingId: this.jobPosting.jobPostingId,
          jobTitle: this.jobPosting.jobTitle,
          jobDescription: this.jobPosting.jobDescription,
          orderId: this.gather.orderId,
          marketId: this.jobPosting.marketId,
          countryId: this.jobPosting.countryId,
          isPosted: undefined
        }

        this.gatherApiService.getClientVMSSubInfo(this.gather.orderId).subscribe(clientVMSInfo => {
          if(clientVMSInfo && Object.values(clientVMSInfo.vmsSubGatherInfo).some(value => value === true)) {
            void this.router.navigateByUrl(`/vms-submittal/${this.jobPosting.jobPostingId}?gatheruuid=${this.trackingId}`, { skipLocationChange: true });
          }
          else {
            void this.router.navigateByUrl('/initial-interview?gatheruuid='+ this.trackingId, { skipLocationChange: true });
          }
        }, error => {
          console.error('Error fetching client VMS info: ' + error.toString());
          void this.router.navigateByUrl('/initial-interview?gatheruuid='+ this.trackingId, { skipLocationChange: true });
        });
      },
      (error) => {
        this.loading = false;
        this.errorMessage = 'gather.availability.error';
        console.error('Error saving availability: ' + error.code);
      });
  }

  onFileSelected(event: any) {
    if (!event.target.files || event.target.files['length'] === 0) {
      return;
    }
    this.form.get('resumeUpload').setValue(event.target.files[0]);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  resumeUploadedHandler(event: boolean): void {
    this.resumeUploaded = event;
  }

  resumeUploadingHandler(event: boolean): void {
    this.resumeUploading = event;
  }

  getAvailabilityType(name: string) {
    if(name === 'now') {
      return TalentProfileStartAvailability.NOW;
    }
    if(name === 'withNotice') {
      return TalentProfileStartAvailability.WITH_NOTICE;
    }
    if(name === 'specificDate') {
      return TalentProfileStartAvailability.SPECIFIC_DATE;
    }
  }

  get availabilityType() {
    return this.form.get('availabilityType');
  }

  onAvailabilityDateChange(newDate: Date) {
    this.availabilityDate = newDate;
    this.form.patchValue({
      availabilityDate: newDate
    });
  }
}

